import { Fragment, useMemo, useState } from 'react'
import {
  EmptyState, ErrorState, FilterChip, FloorSection, Identifier, Input,
  LoadingState, NoPermissionState, RoomGrid, RoomLegend, RoomTile, StaleBanner,
  formatCount, formatTime, useLocale,
} from '@hk/ui'
import {
  ApiError,
  nameIn,
  translateError,
  type BoardRoom,
} from '@hk/core'
import { LayoutGrid, Rows3 } from 'lucide-react'
import { STRINGS } from '../i18n/strings'
import { RoomDetail } from './RoomDetail'
import { RoomList } from './RoomList'
import { useRoomBoard } from './useRoomBoard'
import {
  BOARD_FILTERS, applyFilter, conditionOf, filterCounts, groupByFloor,
  type BoardFilterKey,
} from './boardFilters'

/* ============================================================================
   W-20 — the room board.

   The screen a supervisor decides the morning from. It handles all six mandatory
   screen states (Screen Inventory §6), because a screen that only handles loading and
   success is not finished:

     loading         skeleton
     empty           a property with no rooms yet — not an error
     no match        a filter that yields nothing — distinct from empty, because the
                     way out is different: clear the filter, not set up the property
     error           with a retry and a correlation id in small print
     no permission   authenticated, not authorised — its own state, never an error
     stale           data older than ninety seconds, said out loud

   Offline is the handset's state, not this screen's: a transport failure on a desk
   browser is an error with a retry, and pretending otherwise would tell a supervisor
   their work is queued when nothing here queues anything.
   ========================================================================= */

export function RoomBoard({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const [active, setActive] = useState<BoardFilterKey>('ALL')
  const [search, setSearch] = useState('')

  /* ── SPRINT 35: THE OVERRIDE · SPRINT 37: WHAT HAPPENED TO THE ROOM ──────────
     The tile has carried `onSelect` since Sprint 3 and this board never passed one: five
     hundred buttons with a hover lift and a pointer cursor, doing nothing.

     **IT OPENS FOR EVERYONE, AND IN SPRINT 35 IT OPENED ONLY FOR SOMEBODY WHO COULD
     PERFORM THE OVERRIDE.** That was right when the panel was only the override. It is a
     whole room card now, and gating it on `room.overrideToClean` would hide from an
     ordinary supervisor the answer to the question they ask most often. The ACTS are each
     gated, on W-21; what the room is and what happened to it are not — that is the board's
     own data with a time and a name on it, and the property scope is the boundary on the
     server. */
  /* **W-22 IS A VIEW OF THIS SCREEN, NOT A SECOND ONE** (Change Note 08). Same five
     hundred rooms, same search, same chips — a sidebar offering both would make a
     supervisor choose a screen before they have chosen a question. */
  const [asList, setAsList] = useState(false)
  const [selected, setSelected] = useState<BoardRoom | null>(null)
  const [marked, setMarked] = useState<string | null>(null)
  /* **THE TILE OPENS W-21, AND SINCE SPRINT 62 IT IS A SCREEN RATHER THAN A DIALOG.**

     What it opened before had quietly become three screens in a box: the T8 override,
     taking a room off sale, and the room's history. Everything it did now sits on
     `RoomDetail`, with the room's actual facts around it — a supervisor deciding whether
     to force 110 clean can see that a guest arrives at 14:00 and that it was rejected
     twice last week, which is the context that decision never had. */
  const openRoom = (room: BoardRoom) => {
    setMarked(null)
    board.clearMarkCleanError()
    board.clearServiceError()
    setSelected(room)
  }

  const board = useRoomBoard(propertyId)
  const requireInspection = board.data?.requireInspection ?? true

  // Search is applied BEFORE the counts are taken, so the chips count what the
  // supervisor can actually see. Counting the unsearched board would show "Dirty 17"
  // above three tiles.
  const searched = useMemo(() => {
    const rooms = board.data?.rooms ?? []
    const term = search.trim()
    return term ? rooms.filter((r) => r.roomNumber.includes(term)) : rooms
  }, [board.data, search])

  const counts = useMemo(
    () => filterCounts(searched, requireInspection),
    [searched, requireInspection])

  const visible = useMemo(
    () => applyFilter(searched, active, requireInspection),
    [searched, active, requireInspection])

  const groups = useMemo(
    () => groupByFloor(visible, board.data?.floors ?? []),
    [visible, board.data])

  /* `isLoading` alone is NOT enough, and the difference is only visible against a
     server that keeps failing.

     react-query reports `isLoading` whenever a query with no data is fetching — which
     includes every RETRY. So a board that has never loaded goes: skeleton, error,
     skeleton, error, once per refresh interval. A supervisor glancing at it catches the
     skeleton and reads "slow"; the truth is "the database is down" and it is on screen
     for only part of each cycle.

     Once something has failed, the error holds the screen. The retry still happens in
     the background, and a success still replaces it. */
  /* **W-21 TAKES THE WHOLE SCREEN, THE WAY W-121 DOES FROM THE USERS LIST.** Before the
     board's own loading and error branches, deliberately: a room that is already open must
     not be thrown away by a refresh of the list behind it, and the detail screen does its
     own loading. */
  if (selected) {
    return (
      <RoomDetail
        roomId={selected.roomId}
        board={board}
        onClose={() => setSelected(null)}
      />
    )
  }

  if (board.isLoading && !board.error) return <LoadingState rows={6} />

  // An error only takes over the screen when there is NOTHING to show. Once a board has
  // loaded, a failed refresh must not wipe it: the rooms on screen are still the best
  // information anyone has, and a supervisor mid-assignment losing the board to a full
  // error page is worse than a supervisor working from data that is a minute old and
  // says so. The stale banner is what tells them, ninety seconds later.
  if (board.error && !board.data) {
    // Not authorised is NOT an error. The user signed in successfully; they simply may
    // not see this property, and telling them to "try again" would be a lie.
    if (board.error instanceof ApiError && board.error.code === 'permission.denied')
      return <NoPermissionState />

    const translated = translateError(board.error, locale)
    return (
      <ErrorState
        body={translated.message}
        onRetry={board.refetch}
        {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
      />
    )
  }

  const floorLabel = (floorId: string) =>
    board.data?.floors.find((f) => f.floorId === floorId)?.code ?? ''

  /* THE BUILDING'S OWN NAME, IN THE READER'S LANGUAGE, falling back to the Latin one
     rather than to an empty heading — the same rule every other reference name on this
     screen follows. A hotel that has only ever had one building has one heading, which
     is the correct drawing of a one-building hotel rather than a special case. */
  const buildingLabel = (buildingId: string) => {
    const found = board.data?.buildings.find((b) => b.buildingId === buildingId)
    return found ? nameIn(locale, found.name, found.nameAr) : ''
  }

  return (
    <div className="flex flex-col gap-3.5">
      {board.isStale && board.updatedAt && (
        <StaleBanner
          since={formatTime(board.updatedAt, locale)}
          message={t.staleSince.replace('{time}', formatTime(board.updatedAt, locale))}
          refreshLabel={t.refresh}
          onRefresh={board.refetch}
        />
      )}

      <div className="flex flex-wrap items-center gap-2.5">
        <label className="sr-only" htmlFor="board-search">{t.searchLabel}</label>
        <Input
          id="board-search"
          dir="auto"
          className="h-8 w-56 text-[13px]"
          placeholder={t.searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="text-[11.5px] text-muted-fg">
          {formatCount(visible.length, locale)} {t.rooms}
        </span>
        <div className="flex items-center gap-1" role="group" aria-label={t.rlView}>
          {([['grid', false], ['list', true]] as const).map(([which, value]) => (
            <button
              key={which}
              type="button"
              onClick={() => setAsList(value)}
              aria-pressed={asList === value}
              className={`inline-flex items-center gap-1 rounded-hk border px-2 py-1
                          text-[11.5px] font-medium cursor-pointer ${
                asList === value
                  ? 'border-[var(--hk-primary)] text-[var(--hk-primary)]'
                  : 'border-border text-muted-fg hover:bg-muted'}`}
            >
              {which === 'grid'
                ? <LayoutGrid className="size-3" aria-hidden />
                : <Rows3 className="size-3" aria-hidden />}
              {which === 'grid' ? t.rlAsGrid : t.rlAsList}
            </button>
          ))}
        </div>

        {board.updatedAt && (
          <span className="ms-auto text-[11.5px] text-muted-fg">
            {t.updatedAt} <Identifier className="font-normal not-italic text-muted-fg">
              {formatTime(board.updatedAt, locale)}
            </Identifier>
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {BOARD_FILTERS.map((f) => (
          <FilterChip
            key={f.key}
            label={t.filters[f.key]}
            count={counts[f.key]}
            active={active === f.key}
            onClick={() => setActive(f.key)}
            {...(f.swatch ? { swatch: f.swatch } : {})}
          />
        ))}
      </div>

      {searched.length === 0 && search.trim() === '' ? (
        <EmptyState title={t.emptyTitle} body={t.emptyBody} />
      ) : visible.length === 0 ? (
        <EmptyState title={t.noMatchTitle} body={t.noMatchBody} />
      ) : asList ? (
        <RoomList
          rooms={visible}
          floorLabel={floorLabel}
          onOpen={openRoom}
        />
      ) : (
        <div className="flex flex-col gap-5">
          {groups.map((g, i) => (
            <Fragment key={g.floorId}>
              {/* A HEADING WHEN THE BUILDING CHANGES, not a container around each one.
                  The floors arrive already ordered by building, so "changed since the
                  last group" is the whole of the grouping logic — and a tower with no
                  rooms matching the current filter never draws a heading at all, because
                  it has no groups to change to. */}
              {buildingLabel(g.buildingId) !== '' && g.buildingId !== groups[i - 1]?.buildingId && (
                <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-soft mt-1">
                  {buildingLabel(g.buildingId)}
                </h2>
              )}
            <FloorSection
              key={g.floorId}
              floor={<>{t.floor} <Identifier>{floorLabel(g.floorId)}</Identifier></>}
              count={formatCount(g.rooms.length, locale)}
            >
              <RoomGrid>
                {g.rooms.map((room) => (
                  <RoomTile
                    key={room.roomId}
                    number={room.roomNumber}
                    roomType={nameIn(locale, room.roomType, room.roomTypeAr)}
                    condition={conditionOf(room)}
                    labels={t.tones}
                    /* **NO `selected` ANY MORE, AND THE TYPE CHECKER IS WHAT SAID SO.**
                       It read `selected?.roomId === room.roomId`, which was right while
                       opening a room put a dialog on top of the grid. Opening one now
                       REPLACES the grid, so nothing can be selected while this renders —
                       `selected` is narrowed to null by the branch above, and the
                       comparison was pressed-state nobody could ever see. */
                    onSelect={() => openRoom(room)}
                  />
                ))}
              </RoomGrid>
            </FloorSection>
            </Fragment>
          ))}
        </div>
      )}

      {marked && (
        <div
          role="status"
          className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-[12px]"
        >
          {fill(t.ovrDone, { room: marked })}
        </div>
      )}

      <div className="border-t border-border pt-3">
        <h4 className="mb-1.5 text-[11.5px] font-semibold uppercase tracking-[0.04em] text-muted-fg">
          {t.legendTitle}
        </h4>
        {/* A three-channel encoding is only readable with its key present. The board is
            not complete without this, so it is not behind a toggle. */}
        <RoomLegend
          labels={t.tones}
          occupancyNote={t.legendOccupancy}
          notSellableNote={t.legendNotSellable}
        />
      </div>
    </div>
  )
}

/** Named placeholders, because Arabic and English do not order their clauses the same. */
function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (whole, key: string) => values[key] ?? whole)
}
